import { createCoreApi } from "@gerama/api/core";
import { useSiteAuthStore } from "@gerama/stores";
import { env } from "@/env";
import { getDeviceId } from "@/lib/device-id";

/** Server-side calls (SSR `fetchQuery`) never carry a user token — every page this app renders server-side is public data (widgets feed, catalogue detail, search, blog). Anything that needs the signed-in user's token (subscribe, "my playlists") only ever runs client-side, through `browserCoreApi`. */
export function getCoreApi() {
  return createCoreApi({ baseUrl: env.CORE_API_URL, fetch: cachedFetch });
}

/** Tag every server-side read of the public API carries — `POST /api/revalidate` drops them all when content changes. */
export const CATALOG_TAG = "catalog";

/**
 * Server reads are cached in Next's data cache for a minute and tagged, so a page
 * render doesn't hit the API for data that hasn't changed; the backend's content
 * writes call `/api/revalidate` to drop it early. GET only — everything the
 * server fetches is public, token-less catalogue data.
 */
const cachedFetch: typeof fetch = (input, init) =>
  fetch(input, {
    ...init,
    // Not while `next build` prerenders: pages there are rendered once anyway, and the
    // data-cache options stall the static-generation workers.
    ...((init?.method ?? "GET") === "GET" && process.env.NEXT_PHASE !== "phase-production-build"
      ? { next: { revalidate: 60, tags: [CATALOG_TAG] } }
      : {}),
  });

/** Reads the token from the client-side session store (`packages/stores`'s `useSiteAuthStore`) on every request, and clears the session on a 401 so a stale/expired token doesn't loop. */
export const browserCoreApi = createCoreApi({
  baseUrl: env.NEXT_PUBLIC_CORE_API_URL,
  getToken: () => useSiteAuthStore.getState().token,
  // Lets the feed personalise "made for you" for a visitor who is not signed in.
  headers: (): Record<string, string> => {
    const deviceId = getDeviceId();
    return deviceId ? { "X-Device-Id": deviceId } : {};
  },
  refreshToken: refreshSessionToken,
  onUnauthorized: () => useSiteAuthStore.getState().clearSession(),
});

/**
 * Rolls the stored JWT forward (`POST /auth/refresh`). Used on a 401 by
 * `browserCoreApi` and once per page load by `SessionRefresher`, so an
 * active user isn't signed out when the token expires. A separate client
 * so a failing refresh can't recurse into itself.
 */
export async function refreshSessionToken(): Promise<string | null> {
  const { token, setSession } = useSiteAuthStore.getState();
  if (!token) return null;
  try {
    const result = await createCoreApi({ baseUrl: env.NEXT_PUBLIC_CORE_API_URL }).auth.refreshToken(
      token,
    );
    setSession(result.token, result.user);
    return result.token;
  } catch {
    return null;
  }
}
